/**
 * Types for the Collections & Bookmarks feature (1.5.1)
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const VIRTUAL_COLLECTIONS = {
  ALL: '__all__',
  PINNED: '__pinned__',
} as const

export const SYSTEM_COLLECTION_POSITIONS = {
  ALL: 0,
  PINNED: 1,
  SAVED: 2,
} as const

export const VISIBILITY_OPTIONS = ['all', 'public', 'private', 'draft'] as const

export const SORT_OPTIONS = ['newest', 'oldest', 'most-stars', 'most-used'] as const

export const GRID_LIMITS = {
  INITIAL: 20,
  BATCH: 10,
  MAX_PINNED: 10,
  MAX_COLLECTIONS: 50,
  MAX_BULK_SELECT: 100,
} as const

export const DEBOUNCE_MS = {
  SEARCH: 300,
  DROPDOWN: 100,
} as const

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

export interface Collection {
  id: string
  name: string
  emoji: string
  isPublic: boolean
  isSystem: boolean
  position: number
  promptCount: number
}

export interface PromptGridItem {
  id: string
  userId: string
  title: string
  readmeContent: string
  promptContent: string
  category: string
  visibility: string
  tags: string[]
  starCount: number
  forkCount: number
  commentCount: number
  createdAt: string
  updatedAt: string
  collectionIds: string[]
  isPinned: boolean
  slug: string
  usernameRef: string
}

export interface PromptsGridResult {
  prompts: PromptGridItem[]
  nextCursor: string | null
}

export interface BookmarkState {
  isSaved: boolean
  collectionIds: string[]
}

export type BookmarkStateMap = Record<string, BookmarkState>

export interface BulkActionResult {
  updated: number
  failed: number
  errors: Array<{ itemId: string; reason: string }>
}

// ---------------------------------------------------------------------------
// Filter types
// ---------------------------------------------------------------------------

export type VisibilityFilter = (typeof VISIBILITY_OPTIONS)[number]
export type SortOption = (typeof SORT_OPTIONS)[number]

export interface GridFilters {
  visibility?: VisibilityFilter
  tags?: string[]
  sort?: SortOption
  search?: string
  limit?: number
  cursor?: string
}

export interface PublicProfileFilters {
  sort?: 'newest' | 'most-stars'
  limit?: number
  cursor?: string
}

// ---------------------------------------------------------------------------
// Server Action input/result types
// ---------------------------------------------------------------------------

export interface CreateCollectionInput {
  name: string
  emoji: string
  isPublic?: boolean
}

export interface RenameCollectionInput {
  collectionId: string
  newName: string
}

export interface ReorderCollectionsInput {
  collectionOrder: Array<{ collectionId: string; position: number }>
}

export interface ToggleCollectionPublicInput {
  collectionId: string
  isPublic: boolean
}

export interface AddPromptToCollectionInput {
  promptId: string
  collectionId: string
}

export interface RemovePromptFromCollectionInput {
  promptId: string
  collectionId: string
}

export interface BookmarkPromptInput {
  promptId: string
  targetCollectionIds?: string[]
}

export interface UnbookmarkPromptInput {
  promptId: string
  collectionId?: string
}

export interface BulkAddToCollectionInput {
  promptIds: string[]
  targetCollectionIds: string[]
}

export interface BulkAddTagsInput {
  promptIds: string[]
  tags: string[]
}

export interface BulkChangeVisibilityInput {
  promptIds: string[]
  visibility: 'public' | 'private' | 'draft'
}

export interface BulkDeletePromptsInput {
  promptIds: string[]
}

export interface ReorderPinnedPromptsInput {
  pinnedOrder: Array<{ promptId: string; position: number }>
}
